import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import RecipeDetailHero from '../components/recipe/RecipeDetailHero';
import RecipeMetaPanel from '../components/recipe/RecipeMetaPanel';
import NutritionPanel from '../components/recipe/NutritionPanel';
import IngredientList from '../components/recipe/IngredientList';
import InstructionSteps from '../components/recipe/InstructionSteps';
import {
  getRecipeById,
  deleteRecipe,
  getUserFavorites,
  addToFavorites,
  removeFromFavorites,
} from '../services/recipeService';
import { getRecipeShareText } from '../utils/shareUtils';
import { useLanguage } from '../i18n/LanguageContext';
import './RecipeDetailPage.css';

const RecipeDetailPage = () => {
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const location = useLocation();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const initialRecipe = location.state?.recipe ?? null;

  const [recipe, setRecipe] = useState(initialRecipe);
  const [isLoading, setIsLoading] = useState(!initialRecipe);
  const [error, setError] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [recipeId]);

  // Load favorites for the current user
  useEffect(() => {
    if (!currentUser) {
      setFavoriteIds(null);
      return undefined;
    }
    const controller = new AbortController();
    getUserFavorites(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const ids = (data ?? []).map((r) => r._id || r.id);
        setFavoriteIds(new Set(ids));
      })
      .catch(() => { /* non-critical */ });
    return () => controller.abort();
  }, [currentUser]);

  const handleBackClick = () => {
    const hasHistory = typeof window !== 'undefined' && window.history.length > 1;
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate('/explorer');
    }
  };

  const handleEdit = (r) => {
    const submitPath = lang === 'he' ? '/heb/submit' : '/submit';
    navigate(submitPath, { state: { recipe: r, mode: 'edit' } });
  };

  const handleDelete = async (r) => {
    const id = r?._id || r?.id;
    if (!id) return;
    const shouldDelete = typeof window === 'undefined' || window.confirm(`Delete "${r.title}"?`);
    if (!shouldDelete) return;
    setIsDeleting(true);
    try {
      await deleteRecipe(id);
      navigate(lang === 'he' ? '/heb/explorer' : '/explorer', { replace: true });
    } catch (err) {
      setError(err?.message ?? 'Unable to delete this recipe.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFavoriteToggle = async (r) => {
    const id = r._id || r.id;
    const isFav = favoriteIds?.has(id);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(id); else next.add(id);
      return next;
    });
    try {
      if (isFav) {
        await removeFromFavorites(id);
      } else {
        await addToFavorites(id);
      }
    } catch {
      // Revert on failure
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(id); else next.delete(id);
        return next;
      });
    }
  };

  const handleShare = (r) => {
    const shareText = getRecipeShareText(r, lang);
    const id = r._id || r.id;
    const url =
      window.location.origin +
      window.location.pathname +
      (lang === 'he' ? `#/heb/recipes/${id}` : `#/recipes/${id}`);
    if (navigator.share) {
      navigator.share({ title: r.title, text: shareText, url });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + url)}`, '_blank');
    }
  };

  useEffect(() => {
    if (initialRecipe) {
      setRecipe(initialRecipe);
      setIsLoading(false);
      return undefined;
    }

    if (!recipeId) {
      setError('Recipe not found.');
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadRecipe = async () => {
      setIsLoading(true);
      try {
        const data = await getRecipeById(recipeId, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setRecipe(data);
        setError(null);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setRecipe(null);
        setError(err?.message ?? 'Unable to load this recipe right now.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadRecipe();

    return () => controller.abort();
  }, [initialRecipe, recipeId]);

  const renderContent = () => {
    if (isLoading) {
      return <div className="recipe-detail-state">{t.recipeDetail.loading}</div>;
    }

    if (error) {
      return <div className="recipe-detail-state error">{error}</div>;
    }

    if (!recipe) {
      return <div className="recipe-detail-state">{t.recipeDetail.notFound}</div>;
    }

    const recipeId = recipe._id || recipe.id;
    const isFavorited = favoriteIds ? favoriteIds.has(recipeId) : false;

    return (
      <>
        <RecipeDetailHero
          recipe={recipe}
          currentUser={currentUser}
          isFavorited={isFavorited}
          isDeleting={isDeleting}
          onFavoriteToggle={currentUser ? handleFavoriteToggle : null}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onShare={handleShare}
        />
        <div className="recipe-detail-grid">
          <section className="recipe-detail-primary">
            <IngredientList ingredients={recipe.ingredients} />
            <InstructionSteps steps={recipe.steps} />
          </section>
          <aside className="recipe-detail-aside">
            <RecipeMetaPanel recipe={recipe} />
            <NutritionPanel recipe={recipe} />
          </aside>
        </div>
      </>
    );
  };

  return (
    <div className="recipe-detail-page">
      <main className="recipe-detail-main">
        <button type="button" className="detail-back-link" onClick={handleBackClick}>
          <span className="material-symbols-outlined" aria-hidden="true">
            arrow_back
          </span>
          {t.recipeDetail.backLabel}
        </button>
        {renderContent()}
      </main>
      <PageFooter />
    </div>
  );
};

export default RecipeDetailPage;
